import { useState, useEffect, useCallback, useRef } from 'react';

import { apiService } from '../services/apiService';
import type { SchedulingBoardEntry } from '../services/types';
import {
  getCachedWeek,
  setCachedWeek,
  clearScheduleCache,
  formatDateKey,
  getWeekMonday,
  getWeekSunday,
} from '../utils/scheduleCache';

// All available resource group filters
const ALL_FILTERS = ['TN', 'FN', 'CONVEN'] as const;

interface UseScheduleDataResult {
  data: SchedulingBoardEntry[];
  isLoadingCurrent: boolean;
  /** True when fetching any data in the background (any filter, any week) */
  isLoadingBackground: boolean;
  error: string | null;
  refetch: (forceRefresh?: boolean) => void;
  /** Fetch additional week if not already loaded */
  ensureWeekLoaded: (weekDate: Date) => void;
}

/**
 * Hook for loading production schedule with aggressive background prefetching.
 *
 * Loading strategy:
 * 1. Fetch current week for selected filter → display immediately
 * 2. Background: Fetch remaining weeks for selected filter
 * 3. Background: Fetch all weeks for ALL other filters
 *
 * This ensures instant switching between filters after initial load.
 */
export function useScheduleData(
  initialDate: Date,
  weeksBefore: number = 2,
  weeksAhead: number = 4,
  resourceGrpID?: string
): UseScheduleDataResult {
  const [data, setData] = useState<SchedulingBoardEntry[]>([]);
  const [isLoadingCurrent, setIsLoadingCurrent] = useState(true);
  const [isLoadingBackground, setIsLoadingBackground] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track loaded weeks per filter: Map<filterID, Set<weekMonday>>
  const loadedWeeksMapRef = useRef<Map<string, Set<string>>>(new Map());
  // In-memory data cache per filter for instant switching
  const dataPerFilterRef = useRef<Map<string, SchedulingBoardEntry[]>>(
    new Map()
  );
  const isMountedRef = useRef(true);
  // Track if background prefetch has started
  const backgroundPrefetchStartedRef = useRef(false);
  // Track current resourceGrpID to detect changes
  const currentResourceGrpIDRef = useRef<string | undefined>(resourceGrpID);
  // Track number of active background operations
  const activeBackgroundOpsRef = useRef(0);
  // Track in-flight fetch requests to prevent duplicate fetches
  const inFlightRequestsRef = useRef<
    Map<string, Promise<SchedulingBoardEntry[]>>
  >(new Map());

  // Helper to increment/decrement background loading counter
  const startBackgroundOp = useCallback(() => {
    activeBackgroundOpsRef.current++;
    if (isMountedRef.current) {
      setIsLoadingBackground(true);
    }
  }, []);

  const endBackgroundOp = useCallback(() => {
    activeBackgroundOpsRef.current = Math.max(
      0,
      activeBackgroundOpsRef.current - 1
    );
    if (activeBackgroundOpsRef.current === 0 && isMountedRef.current) {
      setIsLoadingBackground(false);
    }
  }, []);

  // Helper to get/create loaded weeks set for a filter
  const getLoadedWeeks = useCallback((filterID: string) => {
    if (!loadedWeeksMapRef.current.has(filterID)) {
      loadedWeeksMapRef.current.set(filterID, new Set());
    }
    return loadedWeeksMapRef.current.get(filterID)!;
  }, []);

  // Helper to get/set data for a filter in the in-memory cache
  const getFilterData = useCallback(
    (filterID: string): SchedulingBoardEntry[] => {
      return dataPerFilterRef.current.get(filterID) || [];
    },
    []
  );

  const setFilterData = useCallback(
    (filterID: string, entries: SchedulingBoardEntry[]) => {
      dataPerFilterRef.current.set(filterID, entries);
    },
    []
  );

  const addToFilterData = useCallback(
    (filterID: string, newEntries: SchedulingBoardEntry[]) => {
      const existing = dataPerFilterRef.current.get(filterID) || [];
      const merged = deduplicateEntries([...existing, ...newEntries]);
      dataPerFilterRef.current.set(filterID, merged);
      return merged;
    },
    []
  );

  /**
   * Calculate weeks to load based on initial date and range
   */
  const calculateWeeksToLoad = useCallback(() => {
    const centerMonday = getWeekMonday(initialDate);
    const weeksToLoad: string[] = [];

    for (let i = weeksBefore; i > 0; i--) {
      const monday = new Date(centerMonday);
      monday.setDate(monday.getDate() - i * 7);
      weeksToLoad.push(formatDateKey(monday));
    }
    weeksToLoad.push(formatDateKey(centerMonday));
    for (let i = 1; i <= weeksAhead; i++) {
      const monday = new Date(centerMonday);
      monday.setDate(monday.getDate() + i * 7);
      weeksToLoad.push(formatDateKey(monday));
    }

    return { weeksToLoad, currentWeekMonday: formatDateKey(centerMonday) };
  }, [initialDate, weeksBefore, weeksAhead]);

  /**
   * Fetch a single week's data for a specific filter.
   * Uses in-flight request tracking to prevent duplicate API calls.
   */
  const fetchWeekForFilter = useCallback(
    async (
      weekMonday: string,
      filterID: string,
      forceRefresh: boolean
    ): Promise<SchedulingBoardEntry[]> => {
      // Check cache first
      if (!forceRefresh) {
        const cached = getCachedWeek(weekMonday, filterID);
        if (cached) {
          return cached;
        }
      }

      // Create unique key for this request
      const requestKey = `${filterID}-${weekMonday}`;

      // Check if there's already an in-flight request for this week/filter
      if (!forceRefresh && inFlightRequestsRef.current.has(requestKey)) {
        // Return the existing promise to avoid duplicate fetches
        return inFlightRequestsRef.current.get(requestKey)!;
      }

      // Create the fetch promise
      const fetchPromise = (async () => {
        try {
          // Calculate start and end dates for the week
          const monday = new Date(weekMonday + 'T00:00:00');
          const sunday = getWeekSunday(monday);
          const startDate = formatDateKey(monday);
          const endDate = formatDateKey(sunday);

          // Fetch from API (API already filters out empty ResourceID entries)
          const weekData = await apiService.getProductionSchedule(
            startDate,
            endDate,
            filterID
          );

          // Cache the result
          setCachedWeek(weekMonday, filterID, weekData);

          return weekData;
        } finally {
          // Remove from in-flight tracking when done
          inFlightRequestsRef.current.delete(requestKey);
        }
      })();

      // Track this request
      inFlightRequestsRef.current.set(requestKey, fetchPromise);

      return fetchPromise;
    },
    []
  );

  /**
   * Prefetch all weeks for all filters in background
   */
  const prefetchAllFilters = useCallback(
    async (
      weeksToLoad: string[],
      currentWeekMonday: string,
      primaryFilter: string,
      forceRefresh: boolean
    ) => {
      // Build list of all fetch tasks, prioritizing:
      // 1. Remaining weeks for primary filter
      // 2. All weeks for other filters

      const primaryRemainingWeeks = weeksToLoad.filter(
        (w) => w !== currentWeekMonday
      );
      const otherFilters = ALL_FILTERS.filter((f) => f !== primaryFilter);

      // Start background loading indicator
      startBackgroundOp();

      try {
        // Fetch remaining weeks for primary filter first
        for (const weekMonday of primaryRemainingWeeks) {
          if (!isMountedRef.current) return;
          try {
            const weekData = await fetchWeekForFilter(
              weekMonday,
              primaryFilter,
              forceRefresh
            );
            getLoadedWeeks(primaryFilter).add(weekMonday);

            // Always store in in-memory cache
            if (weekData.length > 0) {
              const merged = addToFilterData(primaryFilter, weekData);
              // Update display if this is the current filter
              if (
                isMountedRef.current &&
                currentResourceGrpIDRef.current === primaryFilter
              ) {
                setData(merged);
              }
            }
          } catch (err) {
            console.error(
              `Failed to fetch week ${weekMonday} for ${primaryFilter}:`,
              err
            );
          }
        }

        // Then fetch all weeks for other filters (in parallel per filter)
        for (const filterID of otherFilters) {
          if (!isMountedRef.current) return;

          // Fetch all weeks for this filter in parallel
          const results = await Promise.allSettled(
            weeksToLoad.map((weekMonday) =>
              fetchWeekForFilter(weekMonday, filterID, forceRefresh)
            )
          );

          // Mark as loaded and store in cache
          const allNewData: SchedulingBoardEntry[] = [];
          results.forEach((result, idx) => {
            getLoadedWeeks(filterID).add(weeksToLoad[idx]);
            if (result.status === 'fulfilled' && result.value.length > 0) {
              allNewData.push(...result.value);
            }
          });
          if (allNewData.length > 0) {
            addToFilterData(filterID, allNewData);
          }
        }
      } finally {
        endBackgroundOp();
      }
    },
    [
      fetchWeekForFilter,
      getLoadedWeeks,
      addToFilterData,
      startBackgroundOp,
      endBackgroundOp,
    ]
  );

  /**
   * Load data for the selected filter
   */
  const loadDataForFilter = useCallback(
    async (filterID: string, forceRefresh: boolean = false) => {
      setError(null);

      const loadedWeeks = getLoadedWeeks(filterID);
      const { weeksToLoad, currentWeekMonday } = calculateWeeksToLoad();

      // If force refresh, clear caches for this filter
      if (forceRefresh) {
        loadedWeeks.clear();
        setFilterData(filterID, []);
      }

      // First, check in-memory cache for instant display
      const memoryData = getFilterData(filterID);
      if (memoryData.length > 0 && !forceRefresh) {
        // Instantly show cached data
        setData(memoryData);
        setIsLoadingCurrent(false);

        // Check if we need to fetch any uncached weeks in background
        const uncachedWeeks = weeksToLoad.filter((w) => !loadedWeeks.has(w));
        if (uncachedWeeks.length > 0) {
          startBackgroundOp();
          Promise.allSettled(
            uncachedWeeks.map((w) => fetchWeekForFilter(w, filterID, false))
          ).then((results) => {
            const newData: SchedulingBoardEntry[] = [];
            results.forEach((result, idx) => {
              loadedWeeks.add(uncachedWeeks[idx]);
              if (result.status === 'fulfilled' && result.value.length > 0) {
                newData.push(...result.value);
              }
            });
            if (newData.length > 0 && isMountedRef.current) {
              const merged = addToFilterData(filterID, newData);
              if (currentResourceGrpIDRef.current === filterID) {
                setData(merged);
              }
            }
            endBackgroundOp();
          });
        }
        return;
      }

      // Check localStorage cache for this filter
      const cachedData: SchedulingBoardEntry[] = [];
      let hasCurrentWeek = false;

      for (const weekMonday of weeksToLoad) {
        const cached = getCachedWeek(weekMonday, filterID);
        if (cached) {
          cachedData.push(...cached);
          loadedWeeks.add(weekMonday);
          if (weekMonday === currentWeekMonday) {
            hasCurrentWeek = true;
          }
        }
      }

      // If we have current week cached, show immediately
      if (hasCurrentWeek && !forceRefresh) {
        const dedupedData = deduplicateEntries(cachedData);
        setFilterData(filterID, dedupedData);
        setData(dedupedData);
        setIsLoadingCurrent(false);

        // Check if we need to fetch any uncached weeks
        const uncachedWeeks = weeksToLoad.filter((w) => !loadedWeeks.has(w));
        if (uncachedWeeks.length > 0) {
          startBackgroundOp();
          Promise.allSettled(
            uncachedWeeks.map((w) => fetchWeekForFilter(w, filterID, false))
          ).then((results) => {
            const newData: SchedulingBoardEntry[] = [];
            results.forEach((result, idx) => {
              loadedWeeks.add(uncachedWeeks[idx]);
              if (result.status === 'fulfilled' && result.value.length > 0) {
                newData.push(...result.value);
              }
            });
            if (newData.length > 0 && isMountedRef.current) {
              const merged = addToFilterData(filterID, newData);
              if (currentResourceGrpIDRef.current === filterID) {
                setData(merged);
              }
            }
            endBackgroundOp();
          });
        }
        return;
      }

      // Need to fetch current week
      setIsLoadingCurrent(true);

      try {
        // Fetch current week first (priority)
        const currentWeekData = await fetchWeekForFilter(
          currentWeekMonday,
          filterID,
          forceRefresh
        );
        loadedWeeks.add(currentWeekMonday);

        if (!isMountedRef.current) return;

        // Always populate the per-filter cache so subsequent switches are
        // instant — but only update the DISPLAYED data if this filter is
        // still the current one (else a stale slow response could clobber
        // the user's new filter selection).
        setFilterData(filterID, deduplicateEntries(currentWeekData));
        if (currentResourceGrpIDRef.current === filterID) {
          setData(getFilterData(filterID));
          setIsLoadingCurrent(false);
        }

        // Start background prefetch for all filters (only once on initial load)
        if (!backgroundPrefetchStartedRef.current || forceRefresh) {
          backgroundPrefetchStartedRef.current = true;
          prefetchAllFilters(
            weeksToLoad,
            currentWeekMonday,
            filterID,
            forceRefresh
          );
        } else {
          // Just fetch remaining weeks for this filter
          const remainingWeeks = weeksToLoad.filter(
            (w) => w !== currentWeekMonday && !loadedWeeks.has(w)
          );
          if (remainingWeeks.length > 0) {
            const results = await Promise.allSettled(
              remainingWeeks.map((w) => fetchWeekForFilter(w, filterID, false))
            );
            const newData: SchedulingBoardEntry[] = [];
            results.forEach((result, idx) => {
              loadedWeeks.add(remainingWeeks[idx]);
              if (result.status === 'fulfilled' && result.value.length > 0) {
                newData.push(...result.value);
              }
            });
            if (newData.length > 0 && isMountedRef.current) {
              const merged = addToFilterData(filterID, newData);
              if (currentResourceGrpIDRef.current === filterID) {
                setData(merged);
              }
            }
          }
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        // Don't surface errors from a filter the user has already moved
        // away from — they'd flash an unrelated error banner.
        if (currentResourceGrpIDRef.current === filterID) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch production schedule'
          );
          setIsLoadingCurrent(false);
        }
      }
    },
    [
      calculateWeeksToLoad,
      fetchWeekForFilter,
      getLoadedWeeks,
      getFilterData,
      setFilterData,
      addToFilterData,
      prefetchAllFilters,
      startBackgroundOp,
      endBackgroundOp,
    ]
  );

  /**
   * Ensure a specific week is loaded (for on-demand fetching when navigating)
   */
  const ensureWeekLoaded = useCallback(
    async (weekDate: Date) => {
      const weekMonday = formatDateKey(getWeekMonday(weekDate));
      const filterID = resourceGrpID || 'TN';
      const loadedWeeks = getLoadedWeeks(filterID);

      // Already loaded
      if (loadedWeeks.has(weekMonday)) {
        return;
      }

      try {
        startBackgroundOp();
        const weekData = await fetchWeekForFilter(weekMonday, filterID, false);
        loadedWeeks.add(weekMonday);

        if (weekData.length > 0 && isMountedRef.current) {
          const merged = addToFilterData(filterID, weekData);
          // Only update the DISPLAYED data if this is still the active
          // filter — a slow response from a filter the user has since
          // switched away from should silently update the cache but not
          // flash old data on screen.
          if (currentResourceGrpIDRef.current === filterID) {
            setData(merged);
          }
        }
      } catch (err) {
        // Silently swallow HTTP 400 — the API rejects dates far from today,
        // and the unloaded weeks just render as empty cells.
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.includes('400')) {
          console.error('Failed to fetch week:', weekMonday, err);
        }
      } finally {
        endBackgroundOp();
      }
    },
    [
      resourceGrpID,
      fetchWeekForFilter,
      getLoadedWeeks,
      addToFilterData,
      startBackgroundOp,
      endBackgroundOp,
    ]
  );

  /**
   * Refetch - clears cache on forceRefresh
   */
  const refetch = useCallback(
    (forceRefresh: boolean = false) => {
      if (forceRefresh) {
        clearScheduleCache();
        loadedWeeksMapRef.current.clear();
        dataPerFilterRef.current.clear();
        backgroundPrefetchStartedRef.current = false;
      }
      loadDataForFilter(resourceGrpID || 'TN', forceRefresh);
    },
    [resourceGrpID, loadDataForFilter]
  );

  // Handle resourceGrpID changes
  useEffect(() => {
    if (currentResourceGrpIDRef.current !== resourceGrpID) {
      currentResourceGrpIDRef.current = resourceGrpID;
      loadDataForFilter(resourceGrpID || 'TN', false);
    }
  }, [resourceGrpID, loadDataForFilter]);

  // Initial load
  useEffect(() => {
    isMountedRef.current = true;
    loadDataForFilter(resourceGrpID || 'TN', false);

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    isLoadingCurrent,
    isLoadingBackground,
    error,
    refetch,
    ensureWeekLoaded,
  };
}

/**
 * Filter and deduplicate entries:
 * 1. Remove entries with empty ResourceID (unassigned operations)
 * 2. Deduplicate by SysRowID
 * 3. Deduplicate by composite key (JobNum+OprSeq+ResourceID) to catch
 *    cases where same operation has different SysRowIDs
 */
function deduplicateEntries(
  entries: SchedulingBoardEntry[]
): SchedulingBoardEntry[] {
  // First filter out entries with empty ResourceID
  const filtered = entries.filter(
    (entry) => entry.ResourceID && entry.ResourceID.trim() !== ''
  );

  // Deduplicate by SysRowID first
  const sysRowIdMap = new Map<string, SchedulingBoardEntry>();
  for (const entry of filtered) {
    sysRowIdMap.set(entry.SysRowID, entry);
  }

  // Then deduplicate by composite key (JobNum+OprSeq+ResourceID)
  // This catches cases where same operation might have different SysRowIDs
  const compositeMap = new Map<string, SchedulingBoardEntry>();
  const sysRowIdEntries = Array.from(sysRowIdMap.values());
  for (const entry of sysRowIdEntries) {
    const compositeKey = `${entry.JobNum}-${entry.OprNum}-${entry.ResourceID}`;
    compositeMap.set(compositeKey, entry);
  }

  return Array.from(compositeMap.values());
}
